<script>
  import { writable } from 'svelte/store';
  import { onMount } from 'svelte';
  import { items, updateItem, removeItem } from './stores/cart.js';
  import { ShoppingCartProduct } from './proto/products_pb.js';
  import Stepper from './Stepper.svelte';
  import EnterDataView from './EnterDataView.svelte';
    import MediaQuery from './MediaQuery.svelte';
    import CheckoutTable from './CheckoutTable.svelte';
    import Fa from 'svelte-fa';
    import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
    import { navigate } from 'svelte-routing';
    import { deliveryDiscountPercentage, hasDiscount, sendEmail } from './stores/products.js';
    import analytics from './services/analytics.js';

  // Store um den letzten aktiven Schritt zu speichern
  const prevActiveStep = writable(-1);

  analytics.trackPageView(window.location.pathname, 'Shopping List Page');

  // Tracking beim Ändern der Schritte
  /**
   * @param {number} step - Der aktuelle Schritt im Checkout-Prozess
   */
  function trackCheckoutStep(step) {
    const stepNames = [
      'Прегледайте продуктите си',
      'Въведете данните си',
      'Потвърдете поръчката си'
    ];
    
    analytics.trackEvent('checkout_step', { 
      step: step + 1, 
      step_name: stepNames[step] 
    });
    
    // Beginnen des Checkouts beim Schritt 1 (Dateneingabe)
    if (step === 0) {
      analytics.trackBeginCheckout({ 
        items: $items, 
        totalValue: itemsTotal 
      }, deliveryArea);
    }
    
    prevActiveStep.set(step);
  }

  onMount(() => {
      items.subscribe(value => {
          console.log(value);
      });
      console.log(typeof items);
  });

  /**
    * @param {ShoppingCartProduct} item
    * @param {Event} e
    */
  function handleUpdateItem(item, e) {
      const quantity = parseInt(e.target.value);
      // int parse quanity
      updateItem(item.getProduct().getId(), quantity);
  }
  /** @type {"Kostinbrod" | "Golyanovtsi" | "Dragovishtitsa" | ""} */
  let deliveryArea = "";
  let deliveryCost = 0;
  /**
    * @param {ShoppingCartProduct[]} items
    * @param {string} deliveryArea 
    */
  $: normalDeliveryNoDiscounts = {
    "Kostinbrod": 4,
    "Golyanovtsi": 4,
    "Dragovishtitsa": 4,
  }
  const freeDeliveryThreshold = 80;

  /**
   * @param {string} deliveryArea
   * @param {number} deliveryDiscountPercentage - The percentage of the delivery cost to be discounted.
   * @returns {number}
   */
  function getDeliveryCost(deliveryArea, deliveryDiscountPercentage) {
    const baseDiscount = (() => {
        if (deliveryArea === "Kostinbrod" && itemsTotal >= freeDeliveryThreshold) {
            return 0;
        }
        // @ts-ignore
        return normalDeliveryNoDiscounts[deliveryArea] ?? 0;
    })();
    return baseDiscount - (baseDiscount * deliveryDiscountPercentage);
  }

  /**
   * Calculates the total delivery cost based on items, delivery area, and any applicable discounts.
   * @param {Array<ShoppingCartProduct>} items - The items in the shopping cart.
   * @param {string} deliveryArea - The area where the items will be delivered.
   * @param {number} deliveryDiscountPercentage - The percentage of the delivery cost to be discounted.
   * @returns {number} - The total cost including items and delivery.
   */
  function calculateTotalDeliveryCost(items, deliveryArea, deliveryDiscountPercentage) {
    deliveryCost = getDeliveryCost(deliveryArea, deliveryDiscountPercentage);

    if (itemsTotal >= freeDeliveryThreshold && deliveryArea === "Kostinbrod") {
        deliveryCost = 0;
    }

    return itemsTotal + deliveryCost;
  }

  // TODO: make the request on the backend also take in the area, now it's all 4, so we dont even send it
  $: deliveryDiscountPrice = $deliveryDiscountPercentage;
  $: itemsTotal = $items.reduce((acc, item) => acc + item.getProduct().getDiscountedprice() * item.getQuantity(), 0);
  $: totalDeliveryCost = calculateTotalDeliveryCost($items, deliveryArea, deliveryDiscountPrice);
  $: childDeliveryCost = deliveryCost;

  let address = "";
  let phoneNumber = "";

  let finishInProgress = false;

  async function onFinished() {
    if (finishInProgress) {
        return;
    }
    finishInProgress = true;

    try {
        const orderId = await sendEmail($items, phoneNumber, address, deliveryArea);
        console.log(orderId);
        
        // Track purchase event
        analytics.trackPurchase({
          orderId: orderId,
          items: $items,
          totalValue: totalDeliveryCost,
          deliveryCost: deliveryCost,
          deliveryArea: deliveryArea
        });
        
        // redirect to success page
        navigate(`/success/${orderId}`);
        items.set([]);
        phoneNumber = "";
        address = "";
        deliveryArea = "";
    } finally {
        finishInProgress = false;
    }
  }
    /**
     * @param {ShoppingCartProduct} item
     */
    function deleteItem(item) {
        const product = item.getProduct();
        if (product) {
            // Track removal from cart
            analytics.trackRemoveFromCart(product, item.getQuantity());
            removeItem(product.getId());
        }
        if ($items.length === 0) {
            navigate('/');
        }
    }
</script>
<style>
    table {
        width: 80%;
        border-collapse: collapse;
    }

    th, td {
        border: 1px solid #dddddd;
        text-align: left;
        padding: 8px;
    }

    th {
        background-color: #f2f2f2;
    }
</style>

<article class="flex py-8 relative">
    <div class="grow"></div>
    <section class="w-[30rem]">
        <Stepper stepCount={3} {onFinished}>
            <div slot="content" let:activeStep let:isValidStep> 
                {#if $prevActiveStep !== activeStep}
                    {trackCheckoutStep(activeStep)}
                {/if}
                
                {#if activeStep === 0}
                    <span class="font-bold text-lg mb-10 block">Прегледайте продуктите си</span>
                    {#each $items as item, index (index)}
                        <!-- Constant height -->
                        <div class="h-36 md:h-36 flex mx-4">
                            <img src={item.getProduct().getUrl()} alt={item.getProduct().getName()} class="h-full aspect-square object-contain inline-block rounded-l p-1"/>
                            <div class="w-8 md:w-20"></div>
                            <div class="my-4">
                                {#if hasDiscount(item.getProduct())}
                                    <div class="text-gray-500 line-through">{item.getProduct().getPrice().toFixed(2)}лв</div>
                                    <div class="text-red-500 font-bold">{item.getProduct().getDiscountedprice().toFixed(2)}лв</div>
                                {:else}
                                    <div class="text-2xl font-bold block">{item.getProduct().getPrice().toFixed(2)}лв</div>
                                {/if}
                                <span class="text-sm block w-12">{item.getProduct().getName()}</span>
                            </div>
                            <div class="grow"></div>
                            <!-- md:w-14 is longer because of the up and down selection on desktop -->
                            <input type="number" min="1" value={item.getQuantity()} class="border border-gray-300 p-2 w-10 md:w-14 h-10 my-auto rounded-xl text-center" on:input={(e) => handleUpdateItem(item, e)} />
                            <div class="ml-2 my-auto" on:click={() => deleteItem(item)}><Fa icon={faTrashCan} size="1x" /></div>
                        </div>
                        <div class="py-4"></div>
                    {/each}
                {:else if activeStep === 1}
                    <span class="font-bold text-lg mb-10 block">Въведете данните си</span>
                    <EnterDataView {isValidStep} bind:address={address} bind:phoneNumber={phoneNumber} />
                    <div class="mt-4">
                        <label for="delivery">Изберете място за доставка:</label>
                        <select id="delivery" bind:value={deliveryArea} class="mt-2 p-2 border-2 border-gray-300 rounded-lg w-full">
                            {#each Object.keys(normalDeliveryNoDiscounts) as area}
                                <option value={area}>{area} - {getDeliveryCost(area, $deliveryDiscountPercentage)}лв</option>
                            {/each}
                        </select>
                        
                        {#if $deliveryDiscountPercentage === 1}
                            <p class="text-sm mt-2 text-red-500">Безплатна доставка - ПЪРВИ КЛИЕНТ</p>
                        {:else}
                            <p class="text-sm mt-2">Безплатна доставка за поръчки над 80лв</p>
                        {/if}
                    </div>
                {:else}
                    <span class="font-bold text-lg mb-10 block">Потвърдете поръчката си</span>
                    <table class="mr-4">
                        <tbody>
                            <tr>
                                <th>Детайл</th>
                                <th>Стойност</th>
                            </tr>
                            <tr>
                                <td>Адрес</td>
                                <td>{address}</td>
                            </tr>
                            <tr>
                                <td>Място за доставка</td>
                                <td>{deliveryArea}</td>
                            </tr>
                            <tr>
                                <td>Телефон</td>
                                <td>{phoneNumber}</td>
                            </tr>
                            <tr>
                                <td>Обща сума</td>
                                <td>{totalDeliveryCost.toFixed(2)}лв</td>
                            </tr>
                            {#if deliveryCost > 0}
                                <tr>
                                    <td>Цена за доставка</td>
                                    <td>{deliveryCost.toFixed(2)}лв</td>
                                </tr>
                            {/if}
                            <tr>
                                <td>Очаквана дата на доставка</td>
                                <td>Днес, след около 30мин</td>
                            </tr>
                            <tr>
                                <td>Начин на плащане</td>
                                <td>Наложен платеж</td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="mb-20 mr-4">
                        <MediaQuery query="(max-width: 1000px)" let:matches>
                            {#if matches}
                                <!-- Separator -->
                                <div class="mx-8 my-10 flex justify-center">
                                    <div class="border-r border-gray-300"></div>
                                </div>
                                <CheckoutTable {deliveryCost} {totalDeliveryCost} {items} normalDeliveryNoDiscounts={0}/>
                            {/if}
                        </MediaQuery>
                    </div>
                {/if}
            </div>
        </Stepper>
    </section>
    <MediaQuery query="(min-width: 1000px)" let:matches>
        {#if matches}
            <!-- Separator -->
            <div class="mx-8 my-10 flex justify-center">
                <div class="border-r border-gray-300"></div>
            </div>
            <CheckoutTable 
                deliveryCost={childDeliveryCost} 
                totalDeliveryCost={totalDeliveryCost} 
                items={items} 
                normalDeliveryNoDiscounts={normalDeliveryNoDiscounts[deliveryArea] ?? 0} 
                deliveryDiscountPercentage={$deliveryDiscountPercentage} 
                showDiscountFields={deliveryArea !== ""}
            />
        {/if}
    </MediaQuery>
    <div class="grow"></div>
</article>